import { useCallback, useEffect, useState } from 'react';
import { API_ROUTES } from '../../../shared/api';
import { authedJson } from './client';
import { useApi } from '../hooks/useApi';
import { flushPending, pendingCount, type PendingSession } from './pendingSessions';

/**
 * Sends anything a dropped connection left behind.
 *
 * Runs once when the app mounts and again whenever the browser says it is back
 * online. Those two cover the real cases: you closed the laptop in a tunnel and
 * opened it at home, or you never closed it and the wifi came back.
 *
 * The timezone offset is the one stored with the session rather than the
 * current one. The streak rolls over at the learner's midnight, so a session
 * finished at 11pm and sent at 9am the next morning has to count for the night
 * before — sending today's offset would credit it to the wrong day and break a
 * streak that was never actually missed.
 */
export function useFlushPending(): { pending: number; flush: () => void } {
  const api = useApi();
  const [pending, setPending] = useState(() => pendingCount());

  const send = useCallback(
    (session: PendingSession) => {
      const body = JSON.stringify({
        results: session.results,
        tzOffsetMinutes: session.tzOffsetMinutes,
      });
      const route = session.sectionId
        ? API_ROUTES.recordSession(session.sectionId)
        : API_ROUTES.recordReviewSession;
      return authedJson<unknown>(route, { method: 'POST', body }, api.getToken, api.doRefresh);
    },
    [api],
  );

  const flush = useCallback(() => {
    if (pendingCount() === 0) return;
    void flushPending(send).finally(() => setPending(pendingCount()));
  }, [send]);

  useEffect(() => {
    flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, [flush]);

  return { pending, flush };
}
